import { FC, FormEvent, useEffect } from 'react';
import { Typography, Box, Button, Divider, Grid2 as Grid } from '@mui/material';

import { PostRequestRecruiterDto } from '@libs/dto';
import { useForm, useSnackbar } from '@carcareers/contexts';
import { TextInput, LocationInput, Select } from '@carcareers/components';
import { useAuth, usePost } from '@carcareers/hooks';

interface RequestRecruiterFormProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export const RequestRecruiterForm: FC<RequestRecruiterFormProps> = ({
  onSuccess = () => undefined,
  onClose = () => undefined,
}) => {
  const { session } = useAuth();
  const { data, setData, setErrors } = useForm<PostRequestRecruiterDto>();
  const { requestRecruiter } = usePost();
  const { toggleSnackbar } = useSnackbar();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const response = await requestRecruiter(data, { onSuccess });

    if (response?.data) {
      toggleSnackbar('success', 'Recruiter requested. Please allow up to 48 hours for a response.');
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  useEffect(() => {
    if (session?.user?.firstName && session?.user?.lastName && !data?.contactName) {
      setData('contactName', `${session.user.firstName} ${session.user.lastName}`);
    }

    if (session?.user?.phone && !data?.contactPhone) {
      setData('contactPhone', session.user.phone);
    }

    if (session?.user?.email && !data?.contactEmail) {
      setData('contactEmail', session.user.email);
    }

    if (!data?.urgency) {
      setData('urgency', 'normal');
    }
  }, [session, data, setData]);

  return (
    <>
      <Typography variant="h4">Request Recruiter Assistance</Typography>
      <Typography variant="body1">
        A CARCAREERS recruiter will reach out to you to discuss your job posting. This is a great option if you are
        looking for a more personalized job posting experience.
      </Typography>
      <Typography variant="body1">Please allow up to 48 hours for a response.</Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" gutterBottom>
        Job Details
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextInput name="jobTitle" label="Job Title" fullWidth required />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <LocationInput name="location" label="Location" fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Select
            name="urgency"
            fullWidth
            options={[
              { label: 'Urgent - Need to hire immediately', value: 'high' },
              { label: 'Normal - Looking to hire within 2-4 weeks', value: 'normal' },
              { label: 'Planning - Hiring in the next 1-3 months', value: 'low' },
            ]}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        Contact Information
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput name="contactName" label="Contact Name" fullWidth required />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput name="contactPhone" label="Phone Number" fullWidth required />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput name="contactEmail" label="Email" type="email" fullWidth required />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextInput name="notes" label="Additional Notes" fullWidth multiline rows={3} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          Submit Request
        </Button>
      </Box>
    </>
  );
};
